import React from 'react';
import { Modal } from 'react-bootstrap';
import { AlertTriangle } from 'lucide-react';

/**
 * Delete confirmation.
 *
 * The wording is deliberately not the prototype's "removes it permanently — anyone holding it will
 * see an error at checkout". `couponDao.deleteCoupon` sets `is_deleted = 1`; the document and its
 * redemption history survive. Saying "permanently" here would be false, and would push directors
 * toward deactivating out of a fear that isn't warranted.
 *
 * What *is* true, and what the copy leads with, is that families holding the code stop being able
 * to redeem it — that is the consequence worth pausing over.
 *
 * Wrapped in react-bootstrap's Modal for the same reason as the form: focus trap, scroll lock and
 * backdrop behaviour consistent with every other dialog in the app.
 *
 * `target` is `{ ids, label, redeemed, anyActive }` rather than a coupon, so one dialog serves both
 * the row's trash icon and the bulk bar. `label` is pre-formatted by the caller — “Summer 25” for a
 * single row, "12 coupons" for a selection — which keeps the plural handling in one place instead
 * of threading a count through every sentence here.
 */
const ConfirmDialog = ({ open, target, busy, onConfirm, onDeactivate, onCancel }) => {
    if (!target) return null;

    const { label, redeemed: redeemedCount, anyActive } = target;
    const count = target.ids.length;
    const redeemed = redeemedCount > 0;
    const noun = count === 1 ? 'code' : 'codes';

    return (
        <Modal
            show={open}
            onHide={() => {
                if (!busy) onCancel();
            }}
            centered
            dialogClassName="coupon-codes-page"
            backdrop={busy ? 'static' : true}
        >
            <div className="cpn-bg-white cpn-p-6">
                <div className="cpn-flex cpn-items-start cpn-gap-3">
                    <span className="cpn-flex cpn-h-10 cpn-w-10 cpn-shrink-0 cpn-items-center cpn-justify-center cpn-rounded-xl cpn-bg-red-50 cpn-text-red-600">
                        <AlertTriangle className="cpn-h-5 cpn-w-5" aria-hidden="true" />
                    </span>
                    <div>
                        <h2 className="cpn-text-base cpn-font-bold cpn-text-neutral-900">
                            Delete {label}?
                        </h2>
                        <div className="cpn-mt-1 cpn-text-sm cpn-leading-relaxed cpn-text-neutral-600">
                            {redeemed ? (
                                <>
                                    {count === 1 ? 'This code has' : 'These codes have'} been redeemed{' '}
                                    <strong>
                                        {redeemedCount} {redeemedCount === 1 ? 'time' : 'times'}
                                    </strong>
                                    . Deleting removes {count === 1 ? 'it' : 'them'} from your list and
                                    families holding {count === 1 ? 'it' : 'them'} will no longer be able to
                                    redeem. Deactivating instead keeps {count === 1 ? 'it' : 'them'} in the
                                    list, so you can turn {count === 1 ? 'it' : 'them'} back on later.
                                </>
                            ) : (
                                <>
                                    This removes the {noun} from your list. Anyone holding{' '}
                                    {count === 1 ? 'it' : 'them'} will no longer be able to redeem.
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="cpn-mt-6 cpn-flex cpn-flex-col cpn-gap-2 sm:cpn-flex-row sm:cpn-justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="cpn-h-10 cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-px-4 cpn-text-sm cpn-font-semibold cpn-text-neutral-700 cpn-transition hover:cpn-bg-neutral-50 disabled:cpn-opacity-50"
                    >
                        Cancel
                    </button>
                    {redeemed && anyActive && (
                        <button
                            type="button"
                            onClick={onDeactivate}
                            disabled={busy}
                            className="cpn-h-10 cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-px-4 cpn-text-sm cpn-font-semibold cpn-text-neutral-800 cpn-transition hover:cpn-bg-neutral-50 disabled:cpn-opacity-50"
                        >
                            Deactivate instead
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className="cpn-h-10 cpn-rounded-xl cpn-bg-red-600 cpn-px-4 cpn-text-sm cpn-font-semibold cpn-text-white cpn-transition hover:cpn-bg-red-700 focus:cpn-outline-none focus-visible:cpn-ring-4 focus-visible:cpn-ring-red-200 disabled:cpn-opacity-50"
                    >
                        {busy ? 'Deleting…' : 'Delete'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ConfirmDialog;
